import api from './api'
import type { GeolocationData, GeolocationFormData, NearbyQuery, GeolocationStats } from '@/types'

export const geolocationService = {
  // Listar geolocalizações
  async getAll(page?: number, limit?: number, entityType?: string, entityId?: number, searchQuery?: string, searchField?: string): Promise<any> {
    const params = new URLSearchParams()
    
    // Sempre adicionar page e limit com valores padrão
    params.append('page', (page || 1).toString())
    params.append('limit', (limit || 10).toString())
    
    // Adicionar parâmetros opcionais apenas se existirem e não forem undefined
    if (entityType && entityType !== undefined) {
      params.append('entity_type', entityType)
    }
    if (entityId && entityId !== undefined) {
      params.append('entity_id', entityId.toString())
    }
    if (searchQuery && searchQuery.trim() && searchQuery !== '') {
      params.append('search', searchQuery.trim())
      if (searchField && searchField !== undefined) {
        params.append('search_field', searchField)
      }
    }

    const url = `/geolocations?${params}`
    console.log('🌐 Chamando API:', url)
    console.log('📋 Parâmetros enviados:', Object.fromEntries(params))
    console.log('🔍 Parâmetros de entrada:', { page, limit, entityType, entityId, searchQuery, searchField })
    
    try {
      const response = await api.get(url)
      console.log('📡 Resposta bruta:', response)
      return response.data
    } catch (error) {
      console.error('❌ Erro na chamada da API:', error)
      throw error
    }
  },

  // Buscar geolocalizações por sigla com paginação
  async searchBySigla(sigla: string, page: number = 1, limit: number = 10): Promise<any> {
    const url = `/geolocations/sigla/${encodeURIComponent(sigla)}/paginated?page=${page}&limit=${limit}`
    console.log('🔍 Buscando por sigla:', url)
    
    try {
      const response = await api.get(url)
      console.log('📡 Resposta da busca por sigla:', response)
      return response.data
    } catch (error) {
      console.error('❌ Erro na busca por sigla:', error)
      throw error
    }
  },

  // Buscar geolocalizações por nome com paginação
  async searchByNome(nome: string, page: number = 1, limit: number = 10): Promise<any> {
    const url = `/geolocations/nome/${encodeURIComponent(nome)}/paginated?page=${page}&limit=${limit}`
    console.log('🔍 Buscando por nome:', url)
    
    try {
      const response = await api.get(url)
      console.log('📡 Resposta da busca por nome:', response)
      return response.data
    } catch (error) {
      console.error('❌ Erro na busca por nome:', error)
      throw error
    }
  },

  // Buscar geolocalizações por termo combinado com paginação
  async searchByTermo(termo: string, page: number = 1, limit: number = 10): Promise<any> {
    const url = `/geolocations/unidade/${encodeURIComponent(termo)}/paginated?page=${page}&limit=${limit}`
    console.log('🔍 Buscando por termo combinado:', url)
    
    try {
      const response = await api.get(url)
      console.log('📡 Resposta da busca por termo:', response)
      return response.data
    } catch (error) {
      console.error('❌ Erro na busca por termo:', error)
      throw error
    }
  },

  // Deletar geolocalização por ID
  async delete(id: number): Promise<void> {
    await api.delete(`/geolocations/${id}`)
  },

  // Criar nova geolocalização
  async create(data: GeolocationFormData, file?: File): Promise<GeolocationData> {
    if (file) {
      const formData = new FormData()
      
      // Adicionar dados do formulário
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString())
        }
      })
      
      // Adicionar arquivo
      formData.append('file', file)
      
      const response = await api.post('/geolocations', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    } else {
      const response = await api.post('/geolocations', data)
      return response.data
    }
  },

  // Atualizar ou criar geolocalização (upsert)
  async upsert(entityType: string, entityId: number, data: Partial<GeolocationFormData>): Promise<GeolocationData> {
    const response = await api.put(`/geolocations/${entityType}/${entityId}`, data)
    return response.data
  },

  // Buscar geolocalização por entidade
  async getByEntity(entityType: string, entityId: number): Promise<GeolocationData> {
    const response = await api.get(`/geolocations/${entityType}/${entityId}`)
    return response.data
  },

  // Buscar geolocalizações próximas
  async getNearby(query: NearbyQuery): Promise<GeolocationData[]> {
    const params = new URLSearchParams()
    params.append('latitude', query.latitude.toString())
    params.append('longitude', query.longitude.toString())
    if (query.radius) params.append('radius', query.radius.toString())
    if (query.limit) params.append('limit', query.limit.toString())

    const response = await api.get(`/geolocations/nearby?${params}`)
    return response.data
  },

  // Buscar geolocalizações por cidade
  async getByCity(city: string, limit?: number): Promise<GeolocationData[]> {
    const params = new URLSearchParams()
    params.append('city', city)
    if (limit) params.append('limit', limit.toString())

    const response = await api.get(`/geolocations/city?${params}`)
    return response.data
  },

  // Remover geolocalização
  async remove(entityType: string, entityId: number): Promise<void> {
    await api.delete(`/geolocations/${entityType}/${entityId}`)
  },

  // Obter estatísticas
  async getStats(): Promise<GeolocationStats> {
    const response = await api.get('/geolocations/stats')
    return response.data
  },

  // Verificar status de permissão de geolocalização
  async checkGeolocationPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unsupported'> {
    if (!navigator.geolocation) {
      return 'unsupported'
    }

    if (!navigator.permissions) {
      return 'prompt' // Navegadores mais antigos
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
      return permission.state
    } catch (error) {
      return 'prompt' // Fallback para navegadores que não suportam permissions API
    }
  },

  // Obter posição atual do usuário
  async getCurrentPosition(options?: {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
  }): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalização não é suportada neste navegador'))
        return
      }

      const defaultOptions = {
        enableHighAccuracy: false, // Começar com false para ser mais rápido
        timeout: 10000, // 10 segundos - tempo suficiente para solicitar permissão
        maximumAge: 0, // Sempre obter nova localização
      }

      const finalOptions = { ...defaultOptions, ...options }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => {
          let errorMessage = 'Erro ao obter localização'
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Permissão para acessar localização foi negada. Permita o acesso nas configurações do navegador.'
              break
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Localização indisponível. Verifique se o GPS está ativado.'
              break
            case error.TIMEOUT:
              errorMessage = 'Tempo limite excedido. Tente novamente.'
              break
            default:
              errorMessage = `Erro desconhecido: ${error.message}`
          }
          
          reject(new Error(errorMessage))
        },
        finalOptions
      )
    })
  },

  // Observar mudanças na posição
  watchPosition(callback: (position: GeolocationPosition) => void, errorCallback?: (error: GeolocationPositionError) => void): number {
    if (!navigator.geolocation) {
      throw new Error('Geolocalização não é suportada neste navegador')
    }

    return navigator.geolocation.watchPosition(
      callback,
      errorCallback,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      }
    )
  },

  // Parar de observar posição
  clearWatch(watchId: number): void {
    navigator.geolocation.clearWatch(watchId)
  },

  // Reverter geocodificação (coordenadas para endereço)
  async reverseGeocode(latitude: number, longitude: number): Promise<any> {
    // Esta é uma implementação simplificada
    // Em produção, você pode usar Google Geocoding API ou similar
    try {
      const response = await fetch(
        `https://api.opencagedata.com/geocode/v1/json?q=${latitude}+${longitude}&key=YOUR_API_KEY`
      )
      const data = await response.json()
      return data.results[0]
    } catch (error) {
      console.warn('Erro ao fazer geocodificação reversa:', error)
      return null
    }
  },
}
